import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useNavigationContext } from "@/hooks/useNavigationContext";
import { useScrollPosition } from "@/hooks/useScrollPosition";
import { cn } from "@/lib/utils";

interface StickySecondaryNavProps {
  onAuthClick?: () => void;
}

const StickySecondaryNav = ({ onAuthClick }: StickySecondaryNavProps) => {
  const navigate = useNavigate();
  const { context, menuConfig, isAuthenticated } = useNavigationContext();
  const { showStickyNav } = useScrollPosition(200);

  // Get sticky items from menu config
  const stickyItems = menuConfig.menuItems.filter(item => 
    menuConfig.stickyItems.includes(item.label)
  );

  const handleCtaClick = () => {
    if (menuConfig.cta.action === 'auth') {
      onAuthClick?.();
    } else if (menuConfig.cta.path) {
      navigate(menuConfig.cta.path);
    }
  };

  return (
    <div
      className={cn(
        "fixed top-16 lg:top-[88px] left-0 right-0 z-40 transition-all duration-300 ease-out",
        showStickyNav 
          ? "translate-y-0 opacity-100" 
          : "-translate-y-full opacity-0 pointer-events-none"
      )}
    >
      <div className="bg-background/95 backdrop-blur-md border-b border-border shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-12">
            {/* Context Label */}
            <div className="flex items-center gap-2">
              <context.icon className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-foreground">
                {context.label}
              </span>
            </div>

            {/* Quick Actions */}
            <div className="flex items-center gap-2">
              {stickyItems.slice(0, 3).map((item) => (
                <Link key={item.path + item.label} to={item.path}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      "h-8 text-xs gap-1.5 text-muted-foreground hover:text-foreground",
                      item.special && "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    )}
                  >
                    <item.icon className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">{item.label}</span>
                  </Button>
                </Link>
              ))}

              {/* CTA Button */}
              <Button
                size="sm"
                className="h-8 text-xs bg-primary hover:bg-primary/90"
                onClick={handleCtaClick}
              >
                {menuConfig.cta.label}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StickySecondaryNav;
